/**
 * The single place the app talks HTTP.
 *
 * Screens never call this directly — they use the typed feature hooks in this folder
 * (`useEnquiries`, `useItems`, …), so a contract change is a one-file change.
 *
 * Auth is the Frappe session cookie (contract §1). In dev, Vite proxies `/api` to the Express
 * mock same-origin; in a static production deploy `apiBase()` makes URLs absolute and the call is
 * cross-origin to Frappe, carrying `X-Frappe-CSRF-Token` on writes.
 * `frappe-react-sdk` owns login/session/realtime (see main.tsx); this client owns the
 * `/api/v1` contract paths, which are Frappe whitelisted methods rather than
 * auto-REST resources.
 *
 * ---- NOT vestigial — task 12 kept this file, deliberately ----
 *
 * The migration to `quoflow.quoflow.quoflow.api.*` (`./bosun.ts`) covers most screens, but six
 * features have no endpoint to move to yet and still call `api.*` here for real:
 *
 *   - `useSuppliers.ts`'s `useCreateSupplier` — inline supplier creation during RFQ triage.
 *   - `useEnquiryMutations.ts`'s `useExtractFile` — "No mail? Upload the customer's list."
 *   - `useMail.ts`'s `useUploadFile` — the compose attachment picker.
 *   - `useQuotation.ts`'s `useQuotedItems` — customer purchase history on `/quote`.
 *   - `useMailExtraction.ts` — the fallback AI-extraction path when `mail.extract_enquiry_items`
 *     fails (real call is on `./frappe.ts`, but this module's `BosunApiError` is its error type).
 *   - `useEnquiryFromMail.ts` — "Create enquiry from this mail" (real calls are on `./frappe.ts`;
 *     same `BosunApiError` type-sharing as above).
 *
 * Plus `SessionProvider.tsx` imports `setActingRole` from here directly (not through a hook) to
 * mirror the role switcher onto the `X-Bosun-Role` header every `api.*` call sends — and, since
 * task 12b, every `bosun.ts` call too, via `getActingRole()` below.
 *
 * See task-12-report.md for the full backend-gap list (what each of these needs from a real
 * `quoflow.*` method) and why deleting this file was explicitly ruled out rather than merely
 * deferred: each caller is a shipping feature, not a leftover.
 */

import type { ApiError, ListResponse } from '../types'
import { withSessionRetry } from './sessionRetry'
import { apiBase } from './apiBase'
import { currentCsrfToken, proxyBearer } from './frappeSession'

export const API_BASE = '/api/v1'

/** Thrown for any non-2xx; carries the contract's error envelope when the body has one. */
export class BosunApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fields?: Record<string, string>
  readonly body?: ApiError['error']

  constructor(status: number, body: ApiError['error'] | undefined, fallback: string) {
    super(body?.message ?? fallback)
    this.name = 'BosunApiError'
    this.status = status
    this.code = body?.code ?? 'UNKNOWN'
    this.fields = body?.fields
    this.body = body
  }
}

/**
 * MOCK-ONLY: the acting role, sent as `X-Bosun-Role` so the mock backend can enforce the
 * role gates the contract specifies (§1) — e.g. 403 on reassigning the purchaser.
 *
 * Production Frappe derives the role from the session cookie and IGNORES this header.
 * It is a dev convenience, never a security boundary — the UI's role gating is UX, and
 * the server is what actually decides.
 *
 * Shared with `./bosun.ts`: both transports send the same header off the same state, so
 * `SessionProvider.tsx` only ever calls `setActingRole` once, regardless of which transport a
 * given hook happens to use. `getActingRole` exists for `bosun.ts` to read it — this module's own
 * `request`/`postForm` below read `actingRole` directly since they're already in scope.
 */
let actingRole: string | null = null
export function setActingRole(role: string | null): void {
  actingRole = role
}
export function getActingRole(): string | null {
  return actingRole
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>

export function buildUrl(path: string, params?: QueryParams): string {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`
  if (!params) return url
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `${url}?${qs}` : url
}

async function parseError(res: Response): Promise<never> {
  let envelope: ApiError['error'] | undefined
  try {
    const body = (await res.json()) as Partial<ApiError>
    envelope = body?.error
  } catch {
    // non-JSON error body — fall back to the status text
  }
  throw new BosunApiError(res.status, envelope, `${res.status} ${res.statusText}`)
}

async function requestOnce<T>(method: string, path: string, init?: { params?: QueryParams; body?: unknown }): Promise<T> {
  const headers: Record<string, string> = {}
  if (init?.body !== undefined) headers['Content-Type'] = 'application/json'
  if (actingRole) headers['X-Bosun-Role'] = actingRole
  // Identity for the production proxy; `null` in dev. See `currentClerkToken`.
  const bearer = await proxyBearer()
  if (bearer) headers['Authorization'] = `Bearer ${bearer}`
  if (method !== 'GET') {
    const csrf = currentCsrfToken()
    if (csrf) headers['X-Frappe-CSRF-Token'] = csrf
  }

  const res = await fetch(apiBase() + buildUrl(path, init?.params), {
    method,
    credentials: 'include',
    headers,
    body: init?.body === undefined ? undefined : JSON.stringify(init.body),
  })
  if (!res.ok) await parseError(res)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

function request<T>(method: string, path: string, init?: { params?: QueryParams; body?: unknown }): Promise<T> {
  return withSessionRetry(() => requestOnce<T>(method, path, init))
}

/**
 * Multipart POST, for the endpoints that take a file rather than JSON
 * (`POST /enquiries/{id}/extract` — contract §5). The browser sets the multipart
 * Content-Type and boundary itself, so we must not set it here.
 */
async function postFormOnce<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {}
  if (actingRole) headers['X-Bosun-Role'] = actingRole
  // Identity for the production proxy; `null` in dev. See `currentClerkToken`.
  const bearer = await proxyBearer()
  if (bearer) headers['Authorization'] = `Bearer ${bearer}`
  const csrf = currentCsrfToken()
  if (csrf) headers['X-Frappe-CSRF-Token'] = csrf

  const res = await fetch(apiBase() + buildUrl(path), { method: 'POST', credentials: 'include', headers, body: form })
  if (!res.ok) await parseError(res)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

function postForm<T>(path: string, form: FormData): Promise<T> {
  return withSessionRetry(() => postFormOnce<T>(path, form))
}

export const api = {
  get: <T>(path: string, params?: QueryParams) => request<T>('GET', path, { params }),
  postForm,
  /** Collection endpoints: unwraps nothing — the `{ data, total, limit, offset }` envelope is the contract. */
  list: <T>(path: string, params?: QueryParams) => request<ListResponse<T>>('GET', path, { params }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body }),
  delete: <T>(path: string) => request<T>('DELETE', path),
}
